import * as React from 'react';
import getCurrentSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import getIsInCheckedMode from 'mini-mail-list-store/lib/utils/getIsInCheckedMode';
import getSelectedItemsCount from '../utils/getSelectedItemsCount';
import listViewStore from 'mini-mail-list-store/lib/store/Store';
import MailMultiSelectHeader from 'mini-mail-list-view/lib/components/MailMultiSelectHeader';
import mailStore from 'owa-mail-store/lib/store/Store';
import MailSurfaceHeader from './MailSurfaceHeader';
import { didReadingPaneHeaderLoadFail } from 'mini-mail-placeholder-components-store/lib/selectors/didReadingPaneHeaderLoadFail';
import { isFullComposeShown } from 'mini-mail-compose-store';
import { isInSearchView } from 'mini-mail-list-store/lib/selectors/isInSearchView';
import { MailReadingPaneHeader } from 'mini-mail-reading-pane-view';
import { MailReadingPaneHeaderErrorBoundary } from 'mini-mail-reading-pane-view/lib/components/MailReadingPaneHeaderErrorBoudary';
import { MailSearchHeader } from 'mini-mail-search-view';
import { observer } from 'mobx-react';
import { PerformanceDatapoint } from 'owa-analytics';
import { trySetFocusOnElement } from 'dom-utils/lib/utils/focusUtils';

interface MailHeaderProps {
    navigationButtonRef: React.RefObject<HTMLElement>;
}

@observer
export default class MailHeader extends React.Component<MailHeaderProps, {}> {
    private navigationButtonRef = this.props.navigationButtonRef
        ? this.props.navigationButtonRef
        : React.createRef<HTMLElement>();
    private searchButtonRef = React.createRef<HTMLElement>();
    private composeButtonRef = React.createRef<HTMLElement>();
    private lastComposeShown: boolean = false;
    private lastSearchBoxShown: boolean = false;
    private searchPerfData: PerformanceDatapoint = null;

    componentDidMount() {
        this.setFocusOnComponentUpdate();
    }

    componentDidUpdate() {
        this.setFocusOnComponentUpdate();
    }

    private setFocusOnComponentUpdate() {
        window.requestAnimationFrame(() => {
            let refToFocusOn: React.RefObject<HTMLElement>;

            if (this.lastComposeShown && !isFullComposeShown()) {
                refToFocusOn = this.composeButtonRef;
            } else if (this.lastSearchBoxShown && !listViewStore.shouldShowSearchBox) {
                refToFocusOn = this.searchButtonRef;
            } else {
                const selectedTableView = getCurrentSelectedTableView();
                if (!selectedTableView?.mailHeaderElementToFocus) {
                    refToFocusOn = this.navigationButtonRef;
                }
            }

            trySetFocusOnElement(refToFocusOn);

            this.lastComposeShown = isFullComposeShown();
            this.lastSearchBoxShown = listViewStore.shouldShowSearchBox;
        });
    }

    private isReadingPaneShown(): boolean {
        return !!mailStore.mailModule.selectedId;
    }

    render() {
        const isInComposeView: boolean = isFullComposeShown();
        const isReadingPaneShown: boolean = this.isReadingPaneShown();
        const isInMultiSelectMode: boolean =
            getIsInCheckedMode() && (isInSearchView() || !(isReadingPaneShown || isInComposeView));

        if (isInMultiSelectMode) {
            return (
                <MailMultiSelectHeader
                    selectedTableView={getCurrentSelectedTableView()}
                    selectedItemsCount={getSelectedItemsCount()}
                />
            );
        } else if (isInSearchView()) {
            return <MailSearchHeader perfDatapoint={this.searchPerfData} />;
        } else if (isReadingPaneShown && !isInComposeView) {
            // Header for Reading Pane
            if (didReadingPaneHeaderLoadFail()) {
                return <MailReadingPaneHeaderErrorBoundary />;
            }
            return <MailReadingPaneHeader />;
        } else if (!isInComposeView) {
            return (
                <MailSurfaceHeader
                    forwardedRefs={{
                        navigationButtonRef: this.navigationButtonRef,
                        searchButtonRef: this.searchButtonRef,
                        composeButtonRef: this.composeButtonRef,
                    }}
                    onSearchStartCallback={(searchPerfData: PerformanceDatapoint) => {
                        this.searchPerfData = searchPerfData;
                    }}
                />
            );
        }

        // Header for compose
        return null;
    }
}
